import React, { useState, useCallback } from 'react';
import { useSwipeable } from 'react-swipeable';
import { ThreadCard } from './ThreadCard';
import { Check, Archive, Star, Heart } from 'lucide-react';

const MobileThreadCard = React.memo(function MobileThreadCard({
  thread,
  onMarkAsRead,
  onArchive,
  onFavorite,
  onQuickLike,
  ...props
}) {
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [, setIsRevealed] = useState(false);

  const handleSwipeMove = useCallback((eventData) => {
    const { deltaX } = eventData;
    const maxSwipe = 120;
    const newOffset = Math.max(-maxSwipe, Math.min(maxSwipe, deltaX));
    setSwipeOffset(newOffset);
    setIsRevealed(Math.abs(newOffset) > 40);
  }, []);

  const handleSwipeEnd = useCallback(() => {
    if (Math.abs(swipeOffset) > 60) {
      if (swipeOffset > 0) {
        // Swiped right - mark as read
        onMarkAsRead?.(thread.id);
      } else {
        // Swiped left - archive
        onArchive?.(thread.id);
      }
    }
    setSwipeOffset(0);
    setIsRevealed(false);
  }, [swipeOffset, thread.id, onMarkAsRead, onArchive]);

  const swipeHandlers = useSwipeable({
    onSwiping: handleSwipeMove,
    onSwiped: handleSwipeEnd,
    preventDefaultTouchmoveEvent: true,
    trackMouse: false,
  });

  const quickActions = [
    {
      icon: <Heart size={20} />,
      action: () => onQuickLike?.(thread.id),
      color: 'bg-red-500',
      label: 'Like',
    },
    {
      icon: <Star size={20} />,
      action: () => onFavorite?.(thread.id),
      color: 'bg-yellow-500',
      label: 'Favorite',
    },
    {
      icon: <Check size={20} />,
      action: () => onMarkAsRead?.(thread.id),
      color: 'bg-green-500',
      label: 'Mark as read',
    },
    {
      icon: <Archive size={20} />,
      action: () => onArchive?.(thread.id),
      color: 'bg-gray-500',
      label: 'Archive',
    },
  ];

  return (
    <div className="md:hidden relative overflow-hidden">
      {/* Left swipe actions */}
      <div 
        className={`absolute left-0 top-0 bottom-0 w-24 bg-green-500 flex items-center justify-center text-white transition-transform duration-200 ${
          swipeOffset > 40 ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <Check size={24} />
      </div>

      {/* Right swipe actions */}
      <div 
        className={`absolute right-0 top-0 bottom-0 w-24 bg-red-500 flex items-center justify-center text-white transition-transform duration-200 ${
          swipeOffset < -40 ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <Archive size={24} />
      </div>

      {/* Main card */}
      <div
        {...swipeHandlers}
        style={{
          transform: `translateX(${swipeOffset}px)`,
          transition: swipeOffset === 0 ? 'transform 0.2s ease-out' : 'none',
        }}
        className="bg-white"
      >
        <ThreadCard {...props} thread={thread} />
      </div>

      {/* Quick actions panel */}
      <div className="flex justify-between p-3 bg-gray-50 border-t">
        {quickActions.map((action, index) => (
          <button
            key={index}
            onClick={action.action}
            className={`${action.color} text-white p-3 rounded-full shadow-md active:scale-95 transition-transform`}
            aria-label={action.label}
          >
            {action.icon}
          </button>
        ))}
      </div>

      {/* Pull-to-refresh indicator could go here */}
    </div>
  );
});

export { MobileThreadCard };